import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignOut, HandGrabbing, ArrowUUpLeft, Users, MapPin, Clock, X, CheckCircle, ArrowSquareOut } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { AttendanceToggle, NoShowBadge, AttendanceSummary } from "../../components/AttendanceToggle";

const SYM = { usd: "$", gbp: "£", eur: "€", cad: "$", aud: "$", nzd: "$", inr: "₹", jpy: "¥" };
const money = (n, c) => `${SYM[(c || "gbp").toLowerCase()] || ""}${Number(n || 0).toFixed(2)}`;
const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_e) { return iso; }
};

export default function InstructorPortal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("mine");
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    try {
      const { data: resp } = await api.get("/instructor/overview");
      setData(resp);
      setError(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const act = async (cls, action) => {
    try {
      await api.post(`/instructor/classes/${cls.id}/${action}`);
      toast.success(action === "claim" ? `You're now teaching ${cls.title}` : `Released ${cls.title}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const signOut = async () => { await logout(); navigate("/login", { replace: true }); };

  const accent = data?.business?.brand_accent_color || "#2E5BA8";
  const now = new Date();
  const upcoming = (data?.classes || []).filter((c) => new Date(c.start) >= now);
  const pastMine = (data?.classes || []).filter((c) => new Date(c.start) < now && c.mine).sort((a, b) => (a.start < b.start ? 1 : -1)).slice(0, 30);
  const mine = upcoming.filter((c) => c.mine);
  const open = upcoming.filter((c) => !c.instructor_id);
  const shown = filter === "mine" ? mine : filter === "open" ? open : filter === "past" ? pastMine : upcoming;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="instructor-portal" style={{ "--brand-accent": accent }}>
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {data?.business?.brand_logo_b64 ? (
              <img src={data.business.brand_logo_b64} alt="" className="h-10 w-10 object-contain" />
            ) : (
              <div className="h-10 w-10 flex items-center justify-center font-display text-xl" style={{ backgroundColor: accent }}>
                <span className="text-zinc-950">{(data?.business?.brand_name || "T").charAt(0).toUpperCase()}</span>
              </div>
            )}
            <div>
              <p className="tracker text-zinc-500 text-[10px]">[ INSTRUCTOR ]</p>
              <p className="font-display text-xl leading-none" data-testid="instructor-business-name">{data?.business?.brand_name || "…"}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-zinc-400" data-testid="instructor-user-name">{user?.name}</span>
            {data?.business?.public_url && (
              <a href={data.business.public_url} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2 !px-3 !text-xs" data-testid="instructor-public-link">
                <ArrowSquareOut size={14} /> Public schedule
              </a>
            )}
            <button onClick={signOut} className="btn-ghost !py-2 !px-3 !text-xs" data-testid="instructor-logout">
              <SignOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="tracker text-brand mb-3">[ Group sessions ]</div>
          <h1 className="font-display text-4xl sm:text-5xl">Your classes.</h1>
          <p className="text-zinc-500 mt-2">Classes assigned to you by {data?.business?.brand_name || "the studio"}. Pick up any open class that suits you.</p>
        </div>

        {error ? (
          <div className="card-flat p-12 text-center" data-testid="instructor-error">
            <p className="font-display text-2xl mb-2">Nothing to show.</p>
            <p className="text-zinc-500 text-sm">{error}</p>
          </div>
        ) : !data ? (
          <div className="card-flat p-16 text-center text-zinc-500 tracker">Loading…</div>
        ) : (
          <>
            <div className="flex gap-1 flex-wrap">
              {[["mine", `Mine (${mine.length})`], ["open", `Open (${open.length})`], ["all", `All upcoming (${upcoming.length})`], ["past", `Taught (${pastMine.length})`]].map(([k, label]) => (
                <button key={k} onClick={() => setFilter(k)}
                  className={`tracker px-3 py-1.5 border ${filter === k ? "border-brand text-white" : "border-white/10 text-zinc-500"}`}
                  data-testid={`instructor-filter-${k}`}>{label}</button>
              ))}
            </div>

            {shown.length === 0 ? (
              <div className="card-flat p-16 text-center" data-testid="instructor-classes-empty">
                <Users size={32} weight="duotone" className="text-brand mx-auto mb-3" />
                <div className="font-display text-2xl mb-2">
                  {filter === "mine" ? "No classes assigned yet." : filter === "open" ? "No open classes." : filter === "past" ? "No classes taught yet." : "No upcoming classes."}
                </div>
                {filter === "mine" && open.length > 0 && (
                  <button onClick={() => setFilter("open")} className="tracker text-brand-light text-[10px] underline" data-testid="instructor-goto-open">
                    {open.length} open class{open.length === 1 ? "" : "es"} you could pick up →
                  </button>
                )}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="instructor-classes-grid">
                {shown.map((c) => (
                  <div key={c.id} className="card-flat p-5 flex flex-col" data-testid={`instructor-class-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="font-display text-xl leading-tight truncate">{c.title}</div>
                        <div className="tracker text-zinc-500 text-[10px] mt-1 inline-flex items-center gap-1"><Clock size={10} /> {fmtWhen(c.start)} · {c.duration_min} min</div>
                        {c.location && <div className="tracker text-zinc-500 text-[10px] inline-flex items-center gap-1"><MapPin size={10} /> {c.location}</div>}
                      </div>
                      <span className={`tracker text-[9px] px-2 py-0.5 border shrink-0 ${c.mine ? "border-brand text-brand-light" : c.instructor_id ? "border-white/10 text-zinc-500" : "border-amber-500/40 text-amber-400"}`} data-testid={`instructor-class-status-${c.id}`}>
                        {c.mine ? "YOURS" : c.instructor_id ? c.instructor_name : "OPEN"}
                      </span>
                    </div>
                    <div className="flex items-end justify-between mt-auto pt-3">
                      <div>
                        <div className="font-display text-2xl money">{money(c.price, c.currency)}</div>
                        {c.mine && <div className="tracker text-[9px] text-brand-light" data-testid={`instructor-class-pay-${c.id}`}>you earn {money(c.instructor_pay, c.currency)}{c.payroll_run_id ? " · paid" : ""}</div>}
                      </div>
                      <span className="tracker text-[9px] px-2 py-0.5 border border-white/10 text-zinc-400" data-testid={`instructor-class-spots-${c.id}`}>
                        {c.booked_count}/{c.capacity} BOOKED
                      </span>
                    </div>
                    <AttendanceSummary cls={c} testId={`instructor-class-attendance-${c.id}`} />
                    <div className="flex gap-2 mt-3">
                      {c.mine ? (
                        <>
                          <button onClick={() => setViewing(c)} className="btn-primary flex-1 justify-center !text-xs" data-testid={`instructor-view-bookings-${c.id}`}>
                            Attendees ({c.booked_count})
                          </button>
                          <button onClick={() => act(c, "release")} className="btn-ghost !text-xs" title="Release this class" data-testid={`instructor-release-${c.id}`}>
                            <ArrowUUpLeft size={14} />
                          </button>
                        </>
                      ) : !c.instructor_id ? (
                        <button onClick={() => act(c, "claim")} className="btn-ghost flex-1 justify-center !text-xs" data-testid={`instructor-claim-${c.id}`}>
                          <HandGrabbing size={14} /> Take this class
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {viewing && <AttendeesModal cls={viewing} onClose={() => setViewing(null)} onChanged={load} />}
    </div>
  );
}

function AttendeesModal({ cls, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const load = () => api.get(`/instructor/classes/${cls.id}/bookings`).then(({ data }) => setData(data)).catch((err) => {
    toast.error(formatApiErrorDetail(err.response?.data?.detail)); onClose();
  });
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [cls.id]);

  const markPaid = async (b) => {
    try {
      await api.post(`/instructor/bookings/${b.id}/mark-paid`);
      toast.success(`${b.name} marked as paid`);
      load(); onChanged();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const active = (data?.bookings || []).filter((b) => b.status !== "cancelled");
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="card-flat w-full max-w-2xl p-8 my-8" onClick={(e) => e.stopPropagation()} data-testid="instructor-attendees-modal">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="tracker text-brand mb-2">[ Attendees ]</div>
            <h2 className="font-display text-3xl">{cls.title}</h2>
            <p className="tracker text-zinc-500 text-[10px] mt-1">{fmtWhen(cls.start)}</p>
          </div>
          <button onClick={onClose} data-testid="instructor-attendees-close"><X size={20} weight="bold" /></button>
        </div>
        {!data ? (
          <p className="text-zinc-500 tracker text-center py-8">Loading…</p>
        ) : active.length === 0 ? (
          <p className="text-zinc-500 text-sm py-8 text-center" data-testid="instructor-attendees-empty">No one booked yet.</p>
        ) : (
          <div className="space-y-2" data-testid="instructor-attendees-list">
            {active.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 flex-wrap" data-testid={`instructor-attendee-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">{b.name} <NoShowBadge booking={b} testPrefix="instructor-noshows" /></div>
                  <div className="font-mono text-[10px] text-zinc-500 truncate">{b.email}{b.phone ? ` · ${b.phone}` : ""}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <AttendanceToggle booking={b} endpoint="/instructor/bookings" onChanged={() => { load(); onChanged(); }} testPrefix="instructor-attendance" />
                  {b.status === "paid" ? (
                    <span className="tracker text-[9px] px-2 py-1 border border-emerald-500/40 text-emerald-400 inline-flex items-center gap-1"><CheckCircle size={10} weight="fill" /> PAID</span>
                  ) : (
                    <button onClick={() => markPaid(b)} className="btn-ghost !py-1.5 !px-3 !text-[10px]" data-testid={`instructor-mark-paid-${b.id}`}>Mark paid</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
